import { get } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";

import type { ClientConfig } from "@platform/dmv-api";

import { AdminContext, ClientContext } from "../App";
import LoadingSpinner from "../components/LoadingSpinner";
import {
  CompanyInformationSection,
  GeneralSection,
  NotificationsSection,
  PolicyLookupCredentialsSection,
  ValidationSection,
  VerificationSection,
  WebhooksSection,
} from "../components/settings";
import { getClientConfig } from "../lib/automatic";
import { getErrorMessage } from "../lib/utility";

const SECTION_IDS = [
  "general",
  "company-information",
  "policy-lookup-credentials",
  "notifications",
  "validation",
  "verification",
  "webhooks",
];

// Only returns sections currently rendered by their feature flags.
const getSectionElements = () =>
  SECTION_IDS.map((id) => document.getElementById(id)).filter(
    (element): element is HTMLElement => element !== null
  );

// Falls back to the first section if the active one was removed.
const getValidActiveSection = (
  elements: HTMLElement[],
  currentSection: string
) =>
  elements.some(({ id }) => id === currentSection)
    ? currentSection
    : elements[0].id;

const updateIntersectingSections = (
  entries: IntersectionObserverEntry[],
  intersectingSections: Set<string>
) => {
  // Observer callbacks only contain sections whose status changed.
  entries.forEach(({ isIntersecting, target }) => {
    if (isIntersecting) intersectingSections.add(target.id);
    else intersectingSections.delete(target.id);
  });
};

// A section becomes active after entering the top fifth of the viewport.
const observeActiveSections = (
  elements: HTMLElement[],
  onActiveSectionChange: (sectionId: string) => void
) => {
  // The closure preserves the full intersection state between callbacks.
  const intersectingSections = new Set<string>();
  const observer = new IntersectionObserver(
    (entries) => {
      updateIntersectingSections(entries, intersectingSections);

      // The last matching ID is the furthest section down the page.
      const activeSection = [...SECTION_IDS]
        .reverse()
        .find((id) => intersectingSections.has(id));
      if (activeSection) onActiveSectionChange(activeSection);
    },
    // Shrinks the observer area to the top fifth of the viewport.
    { rootMargin: "0px 0px -80% 0px" }
  );

  elements.forEach((element) => observer.observe(element));
  // Disconnect when the rendered sections change or the page unmounts.
  return () => observer.disconnect();
};

const Settings = () => {
  const { selectedClient, setClientConfig } = useContext(ClientContext);
  const userRole = useContext(AdminContext);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState(SECTION_IDS[0]);
  const isScrollingRef = useRef(false);

  useEffect(() => {
    if (!selectedClient) return;

    const load = async () => {
      setLoading(true);
      setError(null);
      const [response, fetchError] = await getClientConfig(
        [
          "policyLookup",
          "global",
          "ignition",
          "notification",
          "validation",
          "verification",
        ],
        selectedClient,
        false
      );
      if (fetchError) {
        setError(getErrorMessage(fetchError, "Failed to load settings"));
      } else {
        setData(response);
      }
      setLoading(false);
    };
    void load();
  }, [selectedClient]);

  const config = get(data, "config") as ClientConfig | null;

  const collectCredentialsEnabled = get(
    config,
    "policyLookupConfig.collectCredentialsEnabled",
    false
  );

  const validationEnabled = get(config, "validationConfig.enabled", false);

  // useEffect to highlight whichever section currently straddles a trigger line about a fifth of the way down the viewport, so the nav reflects true scroll position.
  useEffect(() => {
    if (loading || error) return;

    const elements = getSectionElements();
    if (elements.length === 0) return;

    // Keep the nav highlight valid before starting a new observer.
    setActiveSection((currentSection) =>
      getValidActiveSection(elements, currentSection)
    );

    // Wrap setActiveSection to ignore updates during programmatic scrolling.
    const handleActiveSectionChange = (sectionId: string) => {
      if (!isScrollingRef.current) {
        setActiveSection(sectionId);
      }
    };

    return observeActiveSections(elements, handleActiveSectionChange);
  }, [loading, error, collectCredentialsEnabled, validationEnabled]);

  const handleNavClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    isScrollingRef.current = true;
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    // Re-enable observer updates after smooth scroll completes.
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 800);
  };

  const navItemClassName = (id: string) =>
    `px-3 py-2 rounded text-sm ${
      activeSection === id
        ? "bg-gray-100 text-gray-900 font-medium"
        : "text-gray-500 hover:bg-gray-50 hover:text-gray-900"
    }`;

  const handleLogoUpdated = (logoUrl: string) => {
    setData((prev) => (prev ? { ...prev, logoUrl } : prev));
    setClientConfig((prev: any) => ({ ...prev, logoUrl }));
  };

  const handleDisplayNameUpdated = (displayName: string) => {
    setData((prev) => (prev ? { ...prev, displayName } : prev));
    setClientConfig((prev: any) => ({ ...prev, displayName }));
  };

  return (
    <div className="flex flex-col flex-1 h-full px-12 gap-y-8 py-10">
      <div className="flex flex-col gap-y-8 max-w-7xl w-full mx-auto">
        <h1 className="text-2xl font-bold">Settings</h1>

        {loading ? (
          <div className="flex items-center gap-x-3 text-sm text-gray-500">
            <LoadingSpinner />
            <span>Loading settings...</span>
          </div>
        ) : error ? (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
            {error}
          </div>
        ) : (
          <div className="flex gap-x-12 items-start">
            <nav className="hidden lg:flex flex-col gap-y-1 w-52 shrink-0 sticky top-10">
              <a
                href="#general"
                onClick={(e) => handleNavClick(e, "general")}
                className={navItemClassName("general")}
              >
                General
              </a>
              <a
                href="#company-information"
                onClick={(e) => handleNavClick(e, "company-information")}
                className={navItemClassName("company-information")}
              >
                Company Information
              </a>
              {collectCredentialsEnabled && (
                <a
                  href="#policy-lookup-credentials"
                  onClick={(e) => handleNavClick(e, "policy-lookup-credentials")}
                  className={navItemClassName("policy-lookup-credentials")}
                >
                  Policy Lookup Credentials
                </a>
              )}
              <a
                href="#notifications"
                onClick={(e) => handleNavClick(e, "notifications")}
                className={navItemClassName("notifications")}
              >
                Notifications
              </a>
              {validationEnabled && (
                <a
                  href="#validation"
                  onClick={(e) => handleNavClick(e, "validation")}
                  className={navItemClassName("validation")}
                >
                  Validation
                </a>
              )}
              <a
                href="#verification"
                onClick={(e) => handleNavClick(e, "verification")}
                className={navItemClassName("verification")}
              >
                Verification
              </a>
              <a
                href="#webhooks"
                onClick={(e) => handleNavClick(e, "webhooks")}
                className={navItemClassName("webhooks")}
              >
                Webhooks
              </a>
            </nav>

            <div className="flex flex-col gap-y-6 max-w-5xl flex-1">
              {data && (
                <GeneralSection
                  id="general"
                  data={data}
                  defaultClientId={selectedClient}
                  canEdit={userRole === "admin" || userRole === "platform-admin"}
                  onLogoUpdated={handleLogoUpdated}
                  onDisplayNameUpdated={handleDisplayNameUpdated}
                />
              )}

              <CompanyInformationSection
                id="company-information"
                clientId={selectedClient}
                initialCompanyInformation={get(
                  config,
                  "policyLookupConfig.companyInformation",
                  null
                )}
              />

              {collectCredentialsEnabled && (
                <PolicyLookupCredentialsSection
                  id="policy-lookup-credentials"
                  clientId={selectedClient}
                />
              )}

              <NotificationsSection
                id="notifications"
                config={config}
                clientId={selectedClient}
              />

              <ValidationSection
                id="validation"
                config={config}
              />

              <VerificationSection
                id="verification"
                config={config}
                clientId={selectedClient}
              />

              <WebhooksSection
                id="webhooks"
                config={config}
                clientId={selectedClient}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
